import { forwardRef, useImperativeHandle, useMemo } from 'react';

import Loader from 'components/partials/loader/loader';
import Tabs from 'components/partials/tabs/tabs';

import AdmitAdjustmentsTab from './components/admit-adjustments-tab';
import CoaModal from './components/coa-modal';
import CompareEstimatesModal from './components/compare-estimates-modal';
import MetricsSection from './components/metrics-section';
import NonModeledTab from './components/non-modeled-tab';
import OverviewTab from './components/overview-tab';
import { useAdmitAdjustments } from './hooks/use-admit-adjustments';
import { useNonModeledAdjustments } from './hooks/use-non-modeled-adjustments';
import { useResultsStep } from './hooks/use-results-step';
import { formatMetricValue } from './helpers/results-format';
import { RESULTS_TABS, TOTAL_FIRST_YEAR_CARDS } from './results-step.constants';

import type { MetricCardData } from './results-step.types';

interface ResultsStepProps {
  clientId?: string | number;
  readOnly?: boolean;
}

const ResultsStep = forwardRef<unknown, ResultsStepProps>(
  ({ clientId, readOnly }, ref) => {
    const {
      modelId,
      isLoading,
      isError,
      errorMessage,
      hasData,
      activeTab,
      setActiveTab,
      metricCards,
      actualYearLabel,
      overviewRows,
      nonModeledRows,
      showModeledActualYear,
      setShowModeledActualYear,
      showLastYear,
      setShowLastYear,
      isCoaOpen,
      openCoa,
      closeCoa,
      coaGroups,
      isCoaLoading,
      compareEstimates,
      downloadDataset,
      isDownloadingDataset,
      canDownloadDataset,
    } = useResultsStep({ clientId });

    const admitAdjustments = useAdmitAdjustments();
    const nonModeledAdjustments = useNonModeledAdjustments(nonModeledRows);

    const displayMetricCards: MetricCardData[] = useMemo(() => {
      if (!admitAdjustments.isDirtyGlobal || !admitAdjustments.previewResults) {
        return metricCards;
      }

      const previewTotalFirstYear =
        admitAdjustments.previewResults.estimatedResults?.totalFirstYear;

      return TOTAL_FIRST_YEAR_CARDS.map((card) => {
        const previewRaw = card.field
          ? previewTotalFirstYear?.[card.field]
          : null;
        const previewValue = typeof previewRaw === 'number' ? previewRaw : null;
        const baselineCard = metricCards.find((mc) => mc.key === card.key);

        return {
          key: card.key,
          label: card.label,
          value: formatMetricValue(previewValue, card.kind),
          originalValue: baselineCard?.value,
          actualValue: baselineCard?.actualValue,
        };
      });
    }, [
      admitAdjustments.isDirtyGlobal,
      admitAdjustments.previewResults,
      metricCards,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        hasChanges:
          admitAdjustments.isDirtyGlobal || nonModeledAdjustments.isDirtyGlobal,
        save: () => {
          admitAdjustments.handleSaveDraft();
          nonModeledAdjustments.handleSaveDraft();
        },
        discard: () => {
          admitAdjustments.handleRevertAll();
          nonModeledAdjustments.handleRevertAll();
        },
      }),
      [
        admitAdjustments.isDirtyGlobal,
        admitAdjustments.handleSaveDraft,
        admitAdjustments.handleRevertAll,
        nonModeledAdjustments.isDirtyGlobal,
        nonModeledAdjustments.handleSaveDraft,
        nonModeledAdjustments.handleRevertAll,
      ]
    );

    if (!modelId) {
      return (
        <div className="py-8 text-center text-gray-600">
          Save the model first to view results.
        </div>
      );
    }

    if (isError) {
      return (
        <div className="py-8 text-center text-critical-600">{errorMessage}</div>
      );
    }

    return (
      <div className="relative min-h-[400px]" data-testid="results-step">
        <Loader
          loading={isLoading}
          fullScreen={false}
          customClass="!bg-transparent"
          overlay="transparent"
        />

        {!isLoading && hasData && (
          <div className="flex flex-col gap-6">
            <MetricsSection
              cards={displayMetricCards}
              actualYearLabel={actualYearLabel}
              isRecalculating={admitAdjustments.isPreviewing}
              onShowCoa={openCoa}
              isCoaDisabled={isCoaLoading}
              onCompareEstimates={compareEstimates.open}
              canCompare={compareEstimates.canCompare}
              onDownloadDataset={downloadDataset}
              isDownloadingDataset={isDownloadingDataset}
              canDownloadDataset={canDownloadDataset}
            />

            <Tabs
              items={RESULTS_TABS}
              activeTab={activeTab}
              onSelect={(value) => setActiveTab(value as typeof activeTab)}
              data-testid="results-tabs"
            />

            {activeTab === 'overview' && (
              <OverviewTab
                rows={overviewRows}
                showModeledActualYear={showModeledActualYear}
                onShowModeledActualYearChange={setShowModeledActualYear}
              />
            )}

            {activeTab === 'non-modeled' && (
              <NonModeledTab
                nonModeledAdjustments={nonModeledAdjustments}
                showLastYear={showLastYear}
                onShowLastYearChange={setShowLastYear}
                readOnly={readOnly}
              />
            )}

            {activeTab === 'admit-adjustments' && (
              <AdmitAdjustmentsTab
                admitAdjustments={admitAdjustments}
                clientId={clientId}
                readOnly={readOnly}
              />
            )}
          </div>
        )}

        {!isLoading && !hasData && !isError && (
          <div className="py-8 text-center text-gray-600">
            No results are available for this model yet.
          </div>
        )}

        {isCoaOpen && (
          <CoaModal
            coaGroups={coaGroups}
            isLoading={isCoaLoading}
            onClose={closeCoa}
            previousYearLabel="Current year"
            currentYearLabel="Future year"
          />
        )}

        {compareEstimates.isOpen && (
          <CompareEstimatesModal
            currentRow={compareEstimates.currentRow}
            comparisonRowStates={compareEstimates.comparisonRowStates}
            versionOptions={compareEstimates.versionOptions}
            onSelectVersion={compareEstimates.setRowVersionId}
            onAddRow={compareEstimates.addRow}
            onRemoveRow={compareEstimates.removeRow}
            canAddRow={compareEstimates.canAddRow}
            userId={compareEstimates.userId}
            onClose={compareEstimates.close}
          />
        )}
      </div>
    );
  }
);

ResultsStep.displayName = 'ResultsStep';

export default ResultsStep;
